import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import businessInfo from '../businessInfo'

import { ArrowLongRightIcon, CheckIcon } from '@heroicons/react/20/solid'

function Home() {
  return (
    <>
      <Helmet>
        <title>Payroll Expert | Payroll-Partner für Steuerkanzleien</title>
        <meta name="description" content="Lohnabrechnung für Steuerkanzleien und Unternehmen. Termingerecht, zuverlässig, DSGVO-konform. Ab 12€ pro Abrechnung." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-white">
      
      {/* Hero Section - Stark verbessert */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 pt-32 pb-24 px-6 text-white">
        {/* Background Pattern */}
        
        <div className="relative z-10 mx-auto max-w-6xl text-center">
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-black leading-tight mb-6 bg-gradient-to-r from-white via-yellow-100 to-white/80 bg-clip-text text-transparent drop-shadow-2xl">
            Payroll-Partner für <span className="block text-yellow-300 drop-shadow-lg">Steuerkanzleien</span>
          </h1>
          
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto leading-relaxed opacity-95">
            Lohnabrechnungen. Termingerecht. Mandantenschutz.
            <span className="block font-semibold text-yellow-200">Ab 12€ pro Abrechnung.</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Link
              to="/contact"
              className="group bg-gradient-to-r from-yellow-500 to-yellow-600 text-blue-900 px-12 py-6 rounded-2xl font-bold text-xl shadow-2xl hover:shadow-3xl hover:from-yellow-600 hover:scale-[1.02] transition-all duration-300 flex items-center gap-3"
            >
              Jetzt Angebot anfordern 
              <ArrowLongRightIcon className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services"
              className="border-3 border-white/50 px-10 py-5 rounded-2xl font-bold text-lg hover:bg-white hover:text-blue-900 hover:border-transparent transition-all backdrop-blur-sm"
            >
              Leistungen entdecken
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-sm opacity-90">
            <div>✅ GoBD-konform</div>
            <div>✅ DATEV-kompatibel</div>
            <div>✅ DSGVO-sicher</div>
            <div>✅ Elster-zertifiziert</div>
            <div>✅ 99,9% pünktlich</div>
          </div>
        </div>
      </section>

      {/* Warum uns wählen - Optimiert */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <div className="text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent mb-6">
            Warum Kanzleien uns wählen
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Keine Experimente. Nur Ergebnisse. Hier ist, warum wir der erste 
            Ansprechpartner für Payroll sind.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: '⚡',
              title: 'Blitzschnell',
              desc: 'Schnelle Standardabrechnung. Backup bei Ausfällen. Nie wieder Deadlines verpassen.'
            },
            {
              icon: '🔒',
              title: 'Mandantenschutz',
              desc: 'Nahtlose DATEV-Integration. Kein eigener Zugang nötig. 100% Compliance.'
            },
            {
              icon: '💰',
              title: '25% günstiger',
              desc: 'Ab 12€/Abrechnung. Volumenrabatte. Monatlich kündbar. Null Risiko.'
            },
            {
              icon: '🧠',
              title: 'Expertenwissen',
              desc: 'Baulohn, Tarifverträge, 2026-Reformen. Wo KI scheitert, glänzen wir.'
            },
            {
              icon: '👤',
              title: 'Persönlich',
              desc: 'Feste Ansprechpartnerin. Keine Callcenter. Direkter Draht.'
            },
            {
              icon: '📊',
              title: 'Transparenz',
              desc: 'Live-Portal. Echtzeit-Reports. Volle Kontrolle behalten.'
            }
          ].map((item, idx) => (
            <div key={idx} className="group p-8 rounded-3xl bg-white/70 backdrop-blur-sm border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all hover:bg-white">
              <div className="text-4xl mb-6">{item.icon}</div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-900 transition">{item.title}</h4>
              <p className="text-gray-600 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Leistungen Teaser - Link zur Detailseite */}
      <section className="py-24 px-6 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-6xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-black bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent mb-6">
            Alles abgedeckt
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Von der Standardabrechnung bis zur Baulohn-Spezialisierung. 
            Keine Lücke bleibt offen.
          </p>
        </div>
        
        {/* Teaser Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {[
            { title: 'B2B', desc: 'Monatslöhne, SV-Meldungen, Elster', link: '/services#basis' },
            { title: 'B2K', desc: 'DATEV/Agenda/Addison - Integration, Mandantenschutz', link: '/services#kanzlei', featured: true },
            { title: 'B2C', desc: 'Finanzbuchhaltung, Kosten‑ und Ergebnisrechnung, Jahresabschlüsse', link: '/services#premium' }
          ].map((card, idx) => (
            <Link 
              key={idx}
              to={card.link}
              className={`group p-10 rounded-3xl text-center transition-all ${
                card.featured 
                  ? 'bg-gradient-to-br from-yellow-500 to-yellow-600 text-white shadow-2xl hover:shadow-3xl hover:scale-[1.02]' 
                  : 'bg-white border-2 border-gray-200 hover:border-blue-300 hover:shadow-xl hover:-translate-y-2'
              }`}
            >
              <CheckIcon className={`w-16 h-16 mx-auto mb-6 ${card.featured ? 'text-white/80' : 'text-blue-600'}`} />
              <h3 className="text-2xl font-bold mb-4">{card.title}</h3>
              <p className="text-lg mb-8 opacity-90">{card.desc}</p>
              <span className="font-bold group-hover:underline">Details ansehen →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-blue-900 to-indigo-900 text-white text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-black mb-6">
            Bereit für <span className="text-yellow-300">perfekte Payroll?</span>
          </h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto leading-relaxed opacity-90">
            60 Minuten bis zu Ihrem unverbindlichen Angebot. Kein Risiko. Nur Gewinn.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Link 
              to="/contact"
              className="group bg-gradient-to-r from-yellow-500 to-yellow-600 text-blue-900 px-12 py-7 rounded-3xl font-black text-2xl shadow-2xl hover:shadow-3xl hover:from-yellow-600 hover:scale-[1.02] transition-all flex items-center gap-4"
            >
              <span>Kostenloses Angebot</span>
              <ArrowLongRightIcon className="w-7 h-7 group-hover:translate-x-2 transition-transform" />
            </Link>
            <div className="text-lg opacity-90">
              Oder rufen Sie direkt an: <a href={`tel:${businessInfo.phone}`} className="font-bold hover:underline">{businessInfo.phone}</a>
            </div>
          </div>
        </div>
      </section>
      </div>
    </>
  )
}

export default Home


// function Home() {
//   return (
//     <div>
//       <section class="bg-blue-900 text-white text-center py-32 px-6">
//         <h1 class="text-4xl md:text-5xl font-bold mb-4">
//           Payroll-Lösungen für Kanzleien – präzise, termingerecht, verlässlich
//         </h1>
//           
//         <p class="text-lg md:text-xl mb-6">
//           Wir übernehmen Ihre Lohn- und Gehaltsabrechnungen mit höchster
//           Sorgfalt und Erfahrung – von Standardabrechnungen bis zu komplexen
//           Sonderfällen.
//         </p>
//           
//         <a
//           href="login.html"
//           class="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-4 rounded-lg font-semibold transition"
//         >
//                 Jetzt individuelle Beratung anfordern   
//         </a>
//       </section>

//       <section class="max-w-5xl mx-auto py-20 px-6">
//           
//         <h2 class="text-3xl font-bold text-blue-900 text-center mb-8">
//           Erfahrung, die Vertrauen schafft
//         </h2>
//           
//         <p class="mb-6">
//           Als spezialisierter Dienstleister für Kanzleien wissen wir:
//           Lohnabrechnungen sind mehr als Zahlen. Sie sind essenziell für
//           Compliance, Mandantenbindung und interne Abläufe. Mit unserer
//           Kombination aus modernster Software und persönlicher Expertise sorgen
//           wir dafür, dass Ihre Payroll zuverlässig, sicher und effizient
//           abläuft.
//         </p>
//           
//         <ul class="space-y-3 list-none">
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             Langjährige Erfahrung in der Betreuung von Kanzleien
//             unterschiedlicher Größe
//           </li>
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             Fehlerfreie, termingerechte und nachvollziehbare Abrechnungen
//           </li>
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             DSGVO-konforme, sichere und rechtlich abgesicherte Prozesse
//           </li>
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             Persönliche Betreuung durch einen festen Ansprechpartner
//           </li>
//             
//         </ul>
//       </section>

//       <section class="max-w-5xl mx-auto py-20 px-6 bg-white rounded-lg shadow-lg">
//           
//         <h2 class="text-3xl font-bold text-blue-900 text-center mb-6">
//           Unsere Leistungen
//         </h2>
//           
//         <ul class="space-y-3 list-none">
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             Lohn- und Gehaltsabrechnungen für alle Mitarbeiter
//           </li>
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             Verwaltung von Steuern, Sozialabgaben und Meldungen an Behörden
//           </li>
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             Sonderabrechnungen: Boni, Urlaubsgeld, Krankengeld, Sonderzahlungen
//           </li>
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             Digitale Archivierung und Reports für Mandanten
//           </li>
//               
//           <li class="relative pl-8 before:content-['✓'] before:absolute before:left-0 before:text-yellow-500 before:font-bold">
//             Beratung bei komplexen Fällen und Sonderregelungen
//           </li>
//             
//         </ul>
//       </section>

//       <section class="max-w-5xl mx-auto py-20 px-6">
//           
//         <h2 class="text-3xl font-bold text-blue-900 text-center mb-6">
//           Kontakt
//         </h2>
//           
//         <form class="max-w-xl mx-auto space-y-4">
//               
//           <input
//             type="text"
//             placeholder="Kanzleiname"
//             class="w-full p-3 border rounded-lg"
//           />
//               
//           <input
//             type="text"
//             placeholder="Ansprechpartner"
//             class="w-full p-3 border rounded-lg"
//           />
//               
//           <input
//             type="number"
//             placeholder="Anzahl der monatlichen Abrechnungen"
//             class="w-full p-3 border rounded-lg"
//           />
//               
//           <textarea
//             placeholder="Besondere Anforderungen / Nachricht"
//             rows="5"
//             class="w-full p-3 border rounded-lg"
//           ></textarea>
//               
//           <button
//             type="submit"
//             class="w-full bg-blue-900 text-white p-4 rounded-lg hover:bg-blue-800 font-semibold transition"
//           >
//             Beratung anfordern
//           </button>
//             
//         </form>
//       </section>
//     </div>
//   );
// }

// export default Home;